import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    AnalysisTransactionTypeDto,
    CompaniesClient,
    CompanyAccountModelDto,
    CustomTransactionCodeDto,
    TransactionCodesClient,
} from '@treasury/api/bo';
import {
    BillingAnalysisTransactionTypeCategoryId,
    BillingCategory,
    BillingNavOption,
    BillingRoute,
} from './billing.types';
import { BillingAccountEntity } from './billingAccount.entity';
import { TransactionTypeEntity } from './transactionType.entity';

@Injectable()
export class BillingService {
    constructor(
        private readonly transactionCodesClient: TransactionCodesClient,
        private readonly companiesClient: CompaniesClient
    ) {}

    public async getBillingCategoriesForNavMenu(): Promise<BillingNavOption[]> {
        const billingCategories: BillingNavOption[] = [];
        const transactionCodes = await this.getTransactionTypes();
        transactionCodes.forEach(item => {
            if (
                !billingCategories.some(
                    category =>
                        category.categoryName === item.analysisTransactionTypeCategory?.categoryName
                )
            ) {
                billingCategories.push({
                    id: item.analysisTransactionTypeCategory
                        ?.analysisTransactionTypeCategoryId as number,
                    categoryName: item.analysisTransactionTypeCategory
                        ?.categoryName as unknown as BillingCategory,
                });
            }
        });
        return billingCategories.sort((a, b) => a.categoryName.localeCompare(b.categoryName));
    }

    /**
     * @param companyId
     * @param analysisTransactionTypeCategoryId
     * @returns a collection of transaction codes with custom analysis counter codes within a billing category
     */
    public async getCustomTransactionCodesByCategory(
        companyId: string,
        analysisTransactionTypeCategoryId?: BillingAnalysisTransactionTypeCategoryId
    ) {
        if (!analysisTransactionTypeCategoryId) {
            throw new Error('Analysis Transaction Type Category ID is required');
        }
        return (
            await this.transactionCodesClient.transactionCodesGetCustomTransactionCodesForCompany(
                analysisTransactionTypeCategoryId,
                parseInt(companyId)
            )
        ).json();
    }

    public async getTransactionTypes(): Promise<AnalysisTransactionTypeDto[]> {
        return (
            await this.transactionCodesClient.transactionCodesGetAnalysisTransactionTypes()
        ).json();
    }

    /**
     * Returns a general list of all possible transaction types within a billing category
     */
    public async getTransactionCodesByCategory(
        category?: BillingCategory
    ): Promise<TransactionTypeEntity[]> {
        const categories = await this.getTransactionTypes();
        if (!category) {
            return (
                categories.map(
                    (item: AnalysisTransactionTypeDto) => new TransactionTypeEntity(item)
                ) || []
            );
        }
        return (
            categories
                ?.filter(
                    (item: AnalysisTransactionTypeDto) =>
                        item.analysisTransactionTypeCategory?.categoryName === category
                )
                .map((item: AnalysisTransactionTypeDto) => new TransactionTypeEntity(item)) || []
        );
    }

    /**
     *
     * @param category
     * @param id
     * @returns a filtered the transaction code with analysis counter for a specific billing type
     */
    public async getBillingAnalysisDetailsByCategoryAndId(
        category: BillingCategory,
        id: BillingAnalysisTransactionTypeCategoryId
    ) {
        return this.getTransactionCodesByCategory(category).then(
            (result: TransactionTypeEntity[]) => {
                return result.find(entity => {
                    return entity.analysisTransactionTypeId === id;
                });
            }
        );
    }

    /**
     *
     * @param companyId
     * @param transactionCodeId
     * @returns collection of accounts that can be linked to a specific transaction code
     */
    public async getAccountsForTransactionCode(companyId: string, transactionCodeId: number) {
        return (
            await this.companiesClient.companiesGetAccountsForAnalysisTransactionType(
                parseInt(companyId),
                transactionCodeId
            )
        ).json();
    }

    /**
     * @param companyId current company
     * @param BillingRoute the billing category from the url
     * @param transactionId the selected transaction type within the billing category
     * @returns a collection of billing accounts
     */
    public async getCompanyAccountsForTransactionType(
        companyId: number,
        BillingRoute: BillingRoute,
        transactionId: number
    ): Promise<BillingAccountEntity[]> {
        const billingCategory = getCategoryFromType(BillingRoute);
        const customTransactionCodes: CustomTransactionCodeDto[] = await (
            await this.transactionCodesClient.transactionCodesGetCustomTransactionCodesForCompany(
                getCategoryIdFromCategory(billingCategory),
                companyId
            )
        ).json();
        const customTransactionCode = customTransactionCodes.find(
            (account: CustomTransactionCodeDto) => {
                return account.analysisTransactionTypeId === transactionId;
            }
        );
        // to do: solve for no custom transaction code found scenario

        // get accounts from service
        const accounts = (
            await this.companiesClient.companiesGetAccountsForAnalysisTransactionType(
                companyId,
                transactionId
            )
        ).data.map((account: CompanyAccountModelDto) => new BillingAccountEntity(account));

        // each transaction code has a accountCustomTransactionCodes property that stores a collection of accounts
        // if the account number matches any of the accounts in the selected custom transaction code
        // that account is selected
        accounts.forEach((account: BillingAccountEntity) => {
            const billingAccount = customTransactionCode?.accountCustomTransactionCodes?.find(
                code => code.accountNumber === account.number
            );
            if (billingAccount) {
                account.active = billingAccount.active;
            }
        });
        return accounts;
    }

    public async getAllTransactionCodesWithCustomData(
        companyId: string,
        category?: BillingCategory
    ): Promise<TransactionTypeEntity[]> {
        if (!category) {
            throw new Error('billing category is required');
        }
        const billingData = await this.getTransactionCodesByCategory(category);
        const customTransactionCodes = await this.getCustomTransactionCodesByCategory(
            companyId,
            getCategoryIdFromCategory(category)
        );
        billingData.forEach(billingDataItem => {
            const correspondingCode = customTransactionCodes.find(
                (transactionCodeItem: CustomTransactionCodeDto) => {
                    return (
                        transactionCodeItem.analysisTransactionTypeId ===
                        billingDataItem.analysisTransactionTypeId
                    );
                }
            );
            if (correspondingCode) {
                billingDataItem.customTransactionCode = correspondingCode;
            } else {
                billingDataItem.setDefaultCustomTransactionCodes();
            }
        });
        return billingData;
    }

    /**
     * Saves the linked or unlinked status of accounts.
     */
    public async saveAccounts(
        companyId: number,
        analysisTransactionTypeCategoryId: number,
        customTransactionCodes: CustomTransactionCodeDto[]
    ) {
        return this.transactionCodesClient.transactionCodesSaveCustomTransactionCodes(
            companyId,
            analysisTransactionTypeCategoryId,
            customTransactionCodes
        );
    }

    /**
     *
     * @param updatedAnalysisCounter the updated analysis counter code
     * @param transactionCodes all transaction codes within a billing category
     * @param customTransactionCode the selected transaction code to be updated
     * @param companyId current company
     * @param analysisTransactionTypeId the selected transaction type within the billing category
     * @returns
     */
    public async updateAnalysisCounter(
        updatedAnalysisCounter: string,
        transactionCodes: TransactionTypeEntity[],
        customTransactionCode: TransactionTypeEntity,
        companyId: number,
        analysisTransactionTypeId: number
    ) {
        const customTransactionCodes: CustomTransactionCodeDto[] = [];
        transactionCodes.forEach(code => {
            let codeToSend: CustomTransactionCodeDto;
            if (code.analysisTransactionTypeId === analysisTransactionTypeId) {
                codeToSend = {
                    id: customTransactionCode.customTransactionCodeId,
                    analysisTransactionTypeId: customTransactionCode.analysisTransactionTypeId,
                    analysisTransactionTypeCategoryId:
                        customTransactionCode.analysisTransactionTypeCategoryId,
                    transactionCode: updatedAnalysisCounter,
                    accountCustomTransactionCodes: [
                        ...(customTransactionCode.accountCustomTransactionCodes || []),
                    ],
                    active: true,
                };
                customTransactionCodes.push(codeToSend);
            } else if (
                code.customTransactionCode?.transactionCode &&
                code.customTransactionCode.transactionCode !== '0'
            ) {
                codeToSend = {
                    id: code.customTransactionCodeId,
                    analysisTransactionTypeId: code.analysisTransactionTypeId,
                    analysisTransactionTypeCategoryId: code.analysisTransactionTypeCategoryId,
                    transactionCode: code.transactionCode,
                    accountCustomTransactionCodes: [...(code.accountCustomTransactionCodes || [])],
                    active: true,
                };
                customTransactionCodes.push(codeToSend);
            }
        });
        return this.transactionCodesClient.transactionCodesSaveCustomTransactionCodes(
            companyId,
            analysisTransactionTypeId,
            customTransactionCodes
        );
    }
}

const getCategoryIdFromCategory = (
    category: BillingCategory
): BillingAnalysisTransactionTypeCategoryId => {
    switch (category) {
        case BillingCategory.ACH:
            return BillingAnalysisTransactionTypeCategoryId.ACH;
        case BillingCategory.WIRE:
            return BillingAnalysisTransactionTypeCategoryId.WIRE;
        case BillingCategory.TRANSFER:
            return BillingAnalysisTransactionTypeCategoryId.TRANSFER;
        case BillingCategory.POSITIVE_PAY:
            return BillingAnalysisTransactionTypeCategoryId.POSITIVE_PAY;
        case BillingCategory.STOP_PAYMENT:
            return BillingAnalysisTransactionTypeCategoryId.STOP_PAYMENT;
        case BillingCategory.ACCOUNT_RECONCILIATION:
            return BillingAnalysisTransactionTypeCategoryId.ACCOUNT_RECONCILIATION;
        default:
            throw new Error(`Unknown billing category: ${category}`);
    }
};

const getCategoryFromType = (type: BillingRoute): BillingCategory => {
    switch (type) {
        case BillingRoute.ACH:
            return BillingCategory.ACH;
        case BillingRoute.WIRE:
            return BillingCategory.WIRE;
        case BillingRoute.TRANSFER:
            return BillingCategory.TRANSFER;
        case BillingRoute.POSITIVE_PAY:
            return BillingCategory.POSITIVE_PAY;
        case BillingRoute.STOP_PAYMENT:
            return BillingCategory.STOP_PAYMENT;
        case BillingRoute.ACCOUNT_RECONCILIATION:
            return BillingCategory.ACCOUNT_RECONCILIATION;
        default:
            throw new Error(`Unknown billing route: ${type}`);
    }
};
